// Reusable approvals UI — the same request → decide flow used for ticket
// sign-off, generalized so tasks/action items reuse it (0082). Entity-agnostic:
// the parent supplies the roster and the request/decide handlers.

import { useState, type FormEvent } from "react";
import { getCurrentUser } from "../lib/auth";
import { formatDate, timeAgo } from "../lib/format";
import { Alert, Spinner } from "./States";
import type { Assignee } from "../lib/types";

/** Minimal shape shared by ticket + task approval rows. */
export interface ApprovalLike {
  id: string;
  approver_id: string | null;
  approver_name: string | null;
  status: "pending" | "approved" | "denied";
  note: string | null;
  requested_by_name: string | null;
  created_at: number;
  decided_at: number | null;
}

const PILL: Record<string, string> = {
  pending: "pill",
  approved: "pill ok",
  denied: "pill danger",
};

export function ApprovalsPanel({
  approvals,
  roster,
  rosterLoading = false,
  pending,
  error,
  onRequest,
  onDecide,
  readOnly = false,
}: {
  approvals: ApprovalLike[];
  roster: Assignee[];
  rosterLoading?: boolean;
  pending: boolean;
  error?: string | null;
  onRequest: (approverId: string, note: string) => Promise<boolean>;
  onDecide: (id: string, approve: boolean) => Promise<boolean>;
  /** Read-only viewer: show approval status, hide request + approve/deny actions. */
  readOnly?: boolean;
}) {
  const [showRequest, setShowRequest] = useState(false);
  const [approverId, setApproverId] = useState("");
  const [note, setNote] = useState("");
  const me = getCurrentUser();

  async function submitRequest(e: FormEvent) {
    e.preventDefault();
    if (!approverId) return;
    const ok = await onRequest(approverId, note.trim());
    if (ok) {
      setApproverId("");
      setNote("");
      setShowRequest(false);
    }
  }

  const sorted = [...approvals].sort((a, b) => a.created_at - b.created_at);

  return (
    <div className="card">
      <div className="card-head">
        <h3>Approvals</h3>
        {readOnly ? null : (
          <button className="btn ghost small" onClick={() => setShowRequest((s) => !s)}>
            {showRequest ? "Cancel" : "+ Request approval"}
          </button>
        )}
      </div>
      <div className="card-body">
        {error ? <Alert kind="error">{error}</Alert> : null}

        {showRequest ? (
          <form onSubmit={submitRequest} className="approval-request">
            <label className="field">
              <span className="label">Approver</span>
              <select
                value={approverId}
                onChange={(e) => setApproverId(e.target.value)}
                disabled={pending || rosterLoading}
                required
              >
                <option value="">Select an approver…</option>
                {roster.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
              {rosterLoading ? <span className="hint">Loading roster…</span> : null}
            </label>
            <label className="field">
              <span className="label">Note (optional)</span>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="What needs sign-off?"
              />
            </label>
            <button className="btn primary small" type="submit" disabled={pending || !approverId}>
              {pending ? <Spinner label="Requesting…" /> : "Send request"}
            </button>
          </form>
        ) : null}

        {sorted.length === 0 && !showRequest ? (
          <p className="muted">No approvals requested.</p>
        ) : (
          <ul className="approval-list">
            {sorted.map((a) => (
              <li key={a.id} className="approval-row">
                <div className="approval-head">
                  <strong>{a.approver_name ?? a.approver_id}</strong>
                  <span className={PILL[a.status] ?? "pill"}>{a.status}</span>
                </div>
                {a.note ? <div className="muted approval-note">{a.note}</div> : null}
                <div className="faint approval-sub">
                  {a.requested_by_name ? `Requested by ${a.requested_by_name} · ` : ""}
                  {timeAgo(a.created_at)}
                  {a.decided_at ? ` · decided ${formatDate(a.decided_at)}` : ""}
                </div>
                {!readOnly && a.status === "pending" && a.approver_id === me?.id ? (
                  <div className="btn-row" style={{ marginTop: 6 }}>
                    <button className="btn small primary" disabled={pending} onClick={() => void onDecide(a.id, true)}>
                      Approve
                    </button>
                    <button className="btn small danger" disabled={pending} onClick={() => void onDecide(a.id, false)}>
                      Deny
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
